/* 208 §6 — THE COLLECTIVE CASE'S CLIENT LIST, IN ITS ORDER: by name, A→Z, a
   tie falling to the member's place in the case. The name order itself moved
   to src/lib/partyOrder.mjs (216 §2, ruling 71 — one comparator for the screen
   and the server); it is re-exported here, so its callers did not move. The
   list is sorted where it is drawn, so a member just added lands in its place. */

/* 211 Part Eight [RULED 24] — a member's numbers are THIS CASE'S, and its quantum the member's (its own,
   else the client's): src/lib/memberRefs.mjs, the one reader of both. */
import { memberNumbers, memberQuantum, numbersShown } from "./memberRefs.mjs";
import { compareMemberNames } from "./partyOrder.mjs";
export { compareMemberNames, memberNameParts, orderParties } from "./partyOrder.mjs";

/* 216 §4 [RULED 71, 73] — TWO TYPED READERS OF THE ONE ORDER for the collective view's lists, so a caller whose
   rows are untyped reads them through `unknown` (the ESLint ratchet counts every `any` read):
   - `orderIdsByParty`: an id list (a chip list's `sel`) in ruling 71's order, resolved against its pool; an id the
     pool lacks keeps its place after the resolved ones, in its own order;
   - `orderByClient`: rows that carry a `client` (the collective modals' members) by that client. */
const asNamed = (p: unknown): (NamedClient & { id?: number | null }) | null =>
  p && typeof p === "object" ? p : null;
export function orderIdsByParty(ids: readonly unknown[] | null | undefined, pool: readonly unknown[] | null | undefined): unknown[] {
  const list = Array.isArray(ids) ? ids : [];
  const rows = Array.isArray(pool) ? pool : [];
  const found: { id: unknown; row: NamedClient & { id?: number | null } }[] = [];
  const missing: unknown[] = [];
  for (const id of list) {
    const row = asNamed(rows.find((p) => asNamed(p)?.id === id));
    if (row) found.push({ id, row });
    else missing.push(id);
  }
  found.sort((a, b) => compareMemberNames(a.row, b.row) || (a.row.id ?? 0) - (b.row.id ?? 0));
  return [...found.map((f) => f.id), ...missing];
}
export function orderByClient<T extends { client?: unknown }>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => {
    const ca = asNamed(a.client);
    const cb = asNamed(b.client);
    return compareMemberNames(ca, cb) || (ca?.id ?? 0) - (cb?.id ?? 0);
  });
}

/** The fields of a client the name order reads. */
export interface NamedClient {
  type?: string | null;
  lastName?: string | null;
  firstName?: string | null;
  companyName?: string | null;
  name?: string | null;
}

const BASE = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

/* 208 §8 — EVERY COLUMN SORTS, with the app's `{ k, d }` sort state (`d` 1
   ascending, −1 descending; a new column starts ascending, the same column
   flips): the name, the status (in its lifecycle order — the order the status
   filters read), the contract number, the quantum, each tranche's due, the
   total due, the total paid, the balance. A row is sorted WITH its index into
   the server's computed answer (`ai`): its money is read at that index, never
   re-derived from its new place. An empty value comes last either way; a tie
   falls to the name order, then to the member's place in the case. The
   default is §6's order. */

/** One member row as the table sorts it: the link (its client, its status) and its index into the server's answer. */
export interface MemberRow {
  lk: { status?: string | null; contractNumbers?: unknown; quantum?: unknown; client?: (NamedClient & { contractNumber?: string | null; quantum?: number | string | null }) | null };
  ai: number;
}

/** The money of one member as the server computed it (`rcd.perMember[ai]`). */
export interface MemberMoney {
  perRound?: readonly ({ due?: number | null } | null | undefined)[] | null;
  rowDue?: number | null;
  rowPaid?: number | null;
  balance?: number | null;
}

export interface MemberSort {
  k: string;
  d: number;
}

/** The default: §6's order — frozen, since it seeds every list's state and must never be mutated. */
export const MEMBER_SORT_DEFAULT: Readonly<MemberSort> = Object.freeze({ k: "name", d: 1 });

/** The sort key of a tranche's column: `r:<its index in the case's rounds>` (the server's `perRound` index). */
export const trancheSortKey = (roundIndex: number): string => `r:${String(roundIndex)}`;

/** The next sort state for a click on column `k` — the house's two-state contract. */
export const nextMemberSort = (cur: MemberSort, k: string): MemberSort => (cur.k === k ? { k, d: cur.d === 1 ? -1 : 1 } : { k, d: 1 });

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Numbers in direction `d`, an absent value last either way (an empty cell never leads a sorted list). */
const byNumber = (a: number | null, b: number | null, d: number): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return d * (a - b);
};

/** Text in direction `d` (base strength), an empty value last either way. */
const byText = (a: string, b: string, d: number): number => {
  if (a && b) return d * BASE.compare(a, b);
  if (a) return -1;
  return b ? 1 : 0;
};

/** The rows sorted by `sort`, each row's money read at its own index in `perMember`; a new array. */
export function sortMemberRows<R extends MemberRow>(
  rows: readonly R[],
  sort: MemberSort,
  perMember: readonly (MemberMoney | null | undefined)[] | null | undefined,
  statusOrder: readonly string[],
): R[] {
  const d = sort.d === -1 ? -1 : 1;
  const k = sort.k;
  const money = (r: R): MemberMoney | null => perMember?.[r.ai] ?? null;
  const rank = (s: string | null | undefined): number => {
    const i = statusOrder.indexOf(s ?? "");
    return i === -1 ? statusOrder.length : i;
  };
  const primary = (a: R, b: R): number => {
    if (k === "status") return d * (rank(a.lk.status) - rank(b.lk.status));
    // 211 Part Eight — this case's numbers; the member's quantum (its own, else the client's)
    if (k === "contract") return byText(numbersShown(memberNumbers(a.lk)), numbersShown(memberNumbers(b.lk)), d);
    if (k === "quantum") return byNumber(num(memberQuantum(a.lk, a.lk.client)), num(memberQuantum(b.lk, b.lk.client)), d);
    if (k.startsWith("r:")) {
      const i = Number(k.slice(2));
      return byNumber(num(money(a)?.perRound?.[i]?.due), num(money(b)?.perRound?.[i]?.due), d);
    }
    if (k === "due") return byNumber(num(money(a)?.rowDue), num(money(b)?.rowDue), d);
    if (k === "paid") return byNumber(num(money(a)?.rowPaid), num(money(b)?.rowPaid), d);
    if (k === "balance") return byNumber(num(money(a)?.balance), num(money(b)?.balance), d);
    return 0;
  };
  return [...rows].sort((a, b) => {
    const byName = compareMemberNames(a.lk.client, b.lk.client);
    if (k === "name") return d * byName || a.ai - b.ai;
    return primary(a, b) || byName || a.ai - b.ai;
  });
}

/* 208 §7 — A SEARCH, LEFT OF THE STATUS FILTERS: it searches the members'
   names and contract numbers and filters the table, not the counters. Accents
   and case are ignored as the add panel's `norm` did (NFD, the combining
   marks dropped, lower case); every word typed must be found, in any order
   (« dupont jean » finds Jean Dupont). The title is not searched (§6's rule:
   a name is its parts, never « Mevr. »). */
const MARKS = /[\u0300-\u036f]/g;

/** A text folded for matching: accents dropped, lower case, trimmed. */
export const foldName = (s: string | number | null | undefined): string =>
  (s === null || s === undefined ? "" : String(s)).normalize("NFD").replace(MARKS, "").toLowerCase().trim();

/** What a member is found by: its name's parts and its contract numbers, folded — 211 Part Eight: a member
 *  of a case is found by THIS CASE's numbers (`numbers`); without them, the client's own field. */
export function memberSearchText(c: (NamedClient & { contractNumber?: string | null }) | null | undefined, numbers?: readonly string[]): string {
  if (!c) return "";
  const refs = numbers ? [...numbers] : [c.contractNumber];
  return foldName([c.lastName, c.firstName, c.companyName, c.name, ...refs].filter((x) => typeof x === "string" && x.trim() !== "").join(" "));
}

/** Does the member match the typed text? Nothing typed matches everyone. */
export function memberMatches(c: (NamedClient & { contractNumber?: string | null }) | null | undefined, typed: string, numbers?: readonly string[]): boolean {
  const words = foldName(typed).split(/\s+/).filter((x) => x !== "");
  if (!words.length) return true;
  const hay = memberSearchText(c, numbers);
  return words.every((x) => hay.includes(x));
}

/* 208 §5 [RULED 13] — THE ADD WINDOW'S NAME FIELD COMPLETES from the firm's
   clients: by NAME — its parts, the words typed found in any order, accents
   and case ignored as §7's search ignores them; never a contract number, never
   the title. Nothing typed lists nobody. */
export function memberNameMatches(c: NamedClient | null | undefined, typed: string): boolean {
  const words = foldName(typed).split(/\s+/).filter((x) => x !== "");
  if (!words.length || !c) return false;
  const hay = foldName([c.lastName, c.firstName, c.companyName, c.name].filter((x) => typeof x === "string" && x.trim() !== "").join(" "));
  return words.every((x) => hay.includes(x));
}
